import Icon from '@forwardflow/ui/Icons'
import { useEffect, useState } from 'react'
import { ContactBoth } from './ContactBoth'
import CampaignService from '@forwardflow/services/CampaignService'
import { useParams } from 'react-router-dom'
import { useGetActiveLeadSourceDetails } from '@forwardflow/hooks/customHooks'

interface FirstToContactState {
   tags: string[]
   type: string
   stages: any[]
   bufferTime: number | null
}

const FirstToContact = () => {
   const { title } = useParams()
   const { data: firstToContactData } = useGetActiveLeadSourceDetails(
      title as string
   )
   const [activeTab, setActiveTab] = useState<string>('both')

   const [firstToContactState, setFirstToContactState] =
      useState<FirstToContactState>({
         tags: [],
         type: 'Both',
         stages: [],
         bufferTime: 0,
      })

   const [isDataLoaded, setIsDataLoaded] = useState(false)

   useEffect(() => {
      if (firstToContactData?.data?.firstToContact) {
         const ftcData = firstToContactData.data.firstToContact
         setFirstToContactState({
            tags: ftcData.tags || [],
            type: ftcData.type || 'Forward Flow',
            stages: ftcData.stages || [],
            bufferTime: ftcData.bufferTime ?? 0,
         })
         setIsDataLoaded(true)
         setActiveTab(ftcData.type === 'Forward Flow' ? 'forward-flow' : 'both')
      }
   }, [firstToContactData])

   useEffect(() => {
      if (firstToContactState.type && title && isDataLoaded) {
         CampaignService.updateFirstToContact(
            title as string,
            firstToContactState
         )
      }
   }, [firstToContactState, title, isDataLoaded])

   const handleForwardFlowClick = () => {
      setActiveTab('forward-flow')
      setFirstToContactState((prevState) => ({
         ...prevState,
         type: 'Forward Flow',
         tags: [],
         stages: [],
         bufferTime: 0,
      }))
   }

   const handleBothClick = () => {
      setActiveTab('both')
      setFirstToContactState((prevState) => ({
         ...prevState,
         type: 'Both',
         tags: [],
         stages: [],
         bufferTime: 0,
      }))
   }

   const onStageSelect = (selectedStage: string) => {
      setFirstToContactState((prevState) => ({
         ...prevState,
         stages: [selectedStage],
         tags: [],
         bufferTime: 0,
      }))
   }

   const onTagSelect = (selectedTag: string) => {
      setFirstToContactState((prevState) => ({
         ...prevState,
         tags: [selectedTag],
         stages: [],
         bufferTime: 0,
      }))
   }

   const onTimePeriodSelect = (selectedTimePeriod: string) => {
      let bufferTime = 0

      // Handle special cases first
      if (selectedTimePeriod === 'Immediate') {
         bufferTime = 0
      } else {
         // Parse the time period string
         const parts = selectedTimePeriod.split(' ')
         const numberPart = parseInt(parts[0], 10)
         const unit = parts[1]

         if (unit?.startsWith('min')) {
            bufferTime = numberPart
         } else if (unit?.startsWith('hr')) {
            bufferTime = numberPart * 60
         } else {
            // Default case if parsing fails
            bufferTime = 0
         }
      }

      setFirstToContactState((prevState) => ({
         ...prevState,
         tags: [],
         stages: [],
         bufferTime: bufferTime,
      }))
   }

   return (
      <div className="flex flex-col flex-1">
         <div className="flex items-center justify-between py-5 pl-10 pr-4 font-medium">
            Waiting Time
         </div>
         <div className="bg-primary/10">
            <p className="px-10 py-5 border-b border-black/20 text-[12px] font-medium text-[#B98BE6]">
               Select how fast Forward Flow contacts a new lead after creation
            </p>
            <div className="bg-primary/20 text-[18px] font-medium hidden">
               <div
                  onClick={handleForwardFlowClick}
                  className={`flex-1 gap-3 cursor-pointer h-28 flexc ${
                     activeTab === 'forward-flow' ? 'bg-primary' : 'opacity-50'
                  }`}
               >
                  <Icon name="ForwardFlow" className="scale-[2]" />
                  <span>Forward Flow</span>
               </div>
               <div
                  onClick={handleBothClick}
                  className={`flex-1 gap-3 cursor-pointer h-28 flexc ${
                     activeTab === 'both' ? 'bg-primary' : 'opacity-50'
                  }`}
               >
                  <Icon
                     name="ForwardFlow"
                     className="scale-[2] border-r pr-1"
                  />
                  <Icon name="Agent" className="pl-2 scale-[1.20] fill-white" />
                  <span className="w-40 ml-4">
                     Both Forward Flow and Agents
                  </span>
               </div>
            </div>

            <ContactBoth
               onStageSelect={onStageSelect}
               onTagSelect={onTagSelect}
               onTimePeriodSelect={onTimePeriodSelect}
            />
         </div>
      </div>
   )
}

export default FirstToContact
